/**
 * One audio element for every answer, unlocked by the press that asked.
 *
 * Phones only let a page make sound in response to a tap. An answer arrives
 * several seconds after the tap that asked for it — long enough, on an iPhone,
 * for a brand new `Audio()` to be refused, and then the answer is silent with
 * every sign saying the voice worked. The rule is per element, though: an
 * element that has played once during a tap may play again later. So there is
 * exactly one, it plays a moment of silence inside the Help press, and every
 * answer after that plays through it.
 *
 * The same goes for the audio context used to hear when somebody has stopped
 * talking: made and resumed inside the tap, or it stays suspended and hears
 * nothing.
 */

/** A twentieth of a second of silence. */
const SILENCE =
  'data:audio/wav;base64,UklGRsQAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YaAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA';

let element: HTMLAudioElement | null = null;
let unlocked = false;
let context: AudioContext | null = null;
/** Ends whatever is playing now, as if it had finished. */
let finishCurrent: (() => void) | null = null;

function audio(): HTMLAudioElement | null {
  if (typeof Audio === 'undefined') return null;
  if (!element) {
    element = new Audio();
    element.preload = 'auto';
  }
  return element;
}

export function audioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!context) {
    try {
      context = new Ctor();
    } catch {
      return null;
    }
  }
  return context;
}

/** Call from inside a tap, before anything is awaited. Cheap, and safe to call on every press. */
export function unlockAudio(): void {
  const ctx = audioContext();
  if (ctx && ctx.state === 'suspended') void ctx.resume().catch(() => {});
  const a = audio();
  if (!a || unlocked || !a.paused) return;
  a.src = SILENCE;
  const played = a.play();
  if (played) {
    void played.then(() => {
      unlocked = true;
    }).catch(() => {
      /* not a tap after all, or replaced by a real answer already: the next press tries again */
    });
  }
}

/**
 * Play one clip to the end. Resolves when it finishes or is stopped; rejects only
 * when it could not play at all, so the caller can fall back to another voice.
 */
export function playBlob(blob: Blob): Promise<void> {
  const a = audio();
  if (!a) return Promise.reject(new Error('this browser cannot play audio'));
  stopPlayback();
  const url = URL.createObjectURL(blob);
  return new Promise<void>((resolve, reject) => {
    let settled = false;
    const settle = (err?: Error) => {
      if (settled) return;
      settled = true;
      a.onended = null;
      a.onerror = null;
      if (finishCurrent === stop) finishCurrent = null;
      URL.revokeObjectURL(url);
      if (err) reject(err);
      else resolve();
    };
    const stop = () => {
      a.pause();
      settle();
    };
    finishCurrent = stop;
    a.onended = () => settle();
    a.onerror = () => settle(new Error('the audio came back but would not play'));
    a.src = url;
    const played = a.play();
    if (played) {
      void played.catch((e: unknown) => {
        const name = e instanceof Error ? e.name : '';
        // Replaced by the next clip, or stopped: not a failure.
        if (name === 'AbortError') return settle();
        settle(name === 'NotAllowedError' ? new Error('the browser blocked the sound (autoplay) — it needs a tap first') : e instanceof Error ? e : new Error(String(e)));
      });
    }
  });
}

export function stopPlayback(): void {
  finishCurrent?.();
}
